import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { AlertCircle, Bell, BellOff } from 'lucide-react';
import { catalogProductsOf, stockRowsOf } from '../../data/store';
import { CustomSelect } from '../../components/CustomSelect';
import { useWebSocket } from '../../hooks/useWebSocket';

export const InventoryStatusTab = ({ org, branchId }) => {
  const { isConnected } = useWebSocket(org.id);
  const products = useMemo(() => catalogProductsOf(org.id), [org.id]);
  const activeStock = useMemo(() => stockRowsOf(org.id, branchId, 'active'), [org.id, branchId]);
  // const pendingStock = useMemo(() => stockRowsOf(org.id, branchId, 'pending'), [org.id, branchId]);

  const stockStatusList = useMemo(() => {
    const list = [];
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    activeStock.forEach(row => {
      const minThreshold = Number(row.minStockThreshold) || 0;
      const numericQty = Number(row.qty) || 0;
      
      let isExpired = false;
      let isExpiringSoon = false;

      if (row.expiryDate) {
        const parts = row.expiryDate.split('-').map(Number);
        if (parts.length === 3 && !isNaN(parts[0]) && !isNaN(parts[1]) && !isNaN(parts[2])) {
          const expDate = new Date(parts[0], parts[1] - 1, parts[2]);
          const diffDays = Math.round((expDate - today) / (1000 * 60 * 60 * 24));
          if (diffDays < 0) {
            isExpired = true;
          } else if (diffDays <= 3) {
            isExpiringSoon = true;
          }
        }
      }

      let status = 'In Stock';
      let statusType = 'success';

      if (isExpired) {
        status = 'Expired';
        statusType = 'error';
      } else if (numericQty === 0) {
        status = 'Out of Stock';
        statusType = 'error';
      } else if (isExpiringSoon) {
        status = 'Expire Soon';
        statusType = 'warning';
      } else if (minThreshold > 0 && numericQty <= minThreshold) {
        status = 'Low Stock';
        statusType = 'warning';
      }

      if (statusType !== 'success' && statusType !== 'info') {
        const product = products.find(p => p.id === row.productId);
        if (!product) return;
        const variant = product.variants?.find(v => v.id === row.variantId) || { id: '', label: 'Default' };
        
        list.push({
          id: row.id,
          productId: product.id,
          variantId: variant.id,
          name: product.name + (variant.label !== 'Default' ? ` - ${variant.label}` : ''),
          maxCount: minThreshold,
          activeQty: row.qty,
          expiryDate: row.expiryDate || '',
          isExpired,
          isExpiringSoon,
          status,
          statusType
        });
      }
    });
    return list;
  }, [products, activeStock]);


  // const accent = org.theme.primary;

  const [alertInterval, setAlertInterval] = useState(() => {
    return localStorage.getItem('mitra_alert_interval') || '5';
  });

  // Alerts are on unless explicitly switched off, so existing users keep the
  // behaviour they already have. OrgDashboard re-reads both of these on every
  // check, so a change here takes effect on the next tick without a reload.
  const [alertsOn, setAlertsOn] = useState(() => {
    return localStorage.getItem('mitra_alerts_enabled') !== 'off';
  });

  const handleIntervalChange = (val) => {
    setAlertInterval(val);
    localStorage.setItem('mitra_alert_interval', val);
  };

  const toggleAlerts = () => {
    const next = !alertsOn;
    setAlertsOn(next);
    localStorage.setItem('mitra_alerts_enabled', next ? 'on' : 'off');
  };

  const intervalLabel = alertInterval === '1' ? 'every minute' : `every ${alertInterval} minutes`;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col gap-6 w-full">
      <header className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-extrabold tracking-tight text-[#0d0d0d]">Stock Status</h2>
            {!isConnected && (
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-100">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500" aria-hidden="true" />
                <span className="text-[10px] font-extrabold uppercase tracking-wider">Offline</span>
              </span>
            )}
          </div>
        </div>
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
          Monitor products that are running low on inventory
        </p>
      </header>

      {/* Stock alerts. Deliberately a plain row rather than a card: it is a
          setting, not content, so it should not compete with the stock table
          below it. Aligned edge to edge with that table so the page reads as
          one column. Colours come from the org theme; the switch matches the
          house toggle in NotificationSettingsTab. */}
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 -mt-2">
        <div className="flex items-center gap-3">
          {alertsOn
            ? <Bell size={15} style={{ color: 'var(--theme-primary)' }} aria-hidden="true" />
            : <BellOff size={15} className="text-slate-300" aria-hidden="true" />}
          <span className={`text-[11px] font-bold uppercase tracking-[0.14em] transition-colors ${alertsOn ? 'text-slate-600' : 'text-slate-400'}`}>
            Stock Alerts
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={alertsOn}
            aria-label={alertsOn ? 'Turn stock alerts off' : 'Turn stock alerts on'}
            title={alertsOn ? `Notifying ${intervalLabel}` : 'Stock alerts are off'}
            onClick={toggleAlerts}
            className={`w-10 h-5 rounded-full transition-all duration-300 relative focus:outline-none flex items-center shrink-0 ${alertsOn ? 'bg-[var(--theme-primary)]' : 'bg-slate-300'}`}
          >
            <span className={`absolute left-0.5 bg-white w-4 h-4 rounded-full shadow-sm transition-transform duration-300 ease-in-out ${alertsOn ? 'translate-x-5' : 'translate-x-0'}`} />
          </button>
          <span className={`text-[10px] font-semibold uppercase tracking-[0.12em] transition-colors ${alertsOn ? 'text-slate-400' : 'text-slate-300'}`}>
            {alertsOn ? 'On' : 'Off'}
          </span>
        </div>

        <div className="flex items-center gap-2.5">
          <span className={`text-[10px] font-bold uppercase tracking-[0.14em] transition-colors ${alertsOn ? 'text-slate-500' : 'text-slate-300'}`}>
            Notify every
          </span>
          <div className="w-[104px]">
            <CustomSelect
              value={alertInterval}
              onChange={handleIntervalChange}
              disabled={!alertsOn}
              options={[
                { label: '1 Min', value: '1' },
                { label: '5 Mins', value: '5' },
                { label: '15 Mins', value: '15' },
                { label: '30 Mins', value: '30' },
                { label: '45 Mins', value: '45' },
                { label: '60 Mins', value: '60' }
              ]}
            />
          </div>
        </div>
      </div>

      <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg shadow-sm flex flex-col overflow-hidden">
        {stockStatusList.length === 0 ? (
          <div className="py-12 text-center m-6 border border-dashed border-[var(--theme-tertiary)] rounded-lg">
            <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">All products are well stocked.</p>
          </div>
        ) : (
          <div className="overflow-x-auto w-full">
            <table className="w-full text-left text-[11px] whitespace-nowrap">
              <thead className="bg-[var(--theme-secondary)] border-b border-[var(--theme-tertiary)]">
                <tr>
                  <th className="py-3 px-4 font-extrabold uppercase tracking-[0.16em] text-slate-400">Product Name</th>
                  <th className="py-3 px-4 font-extrabold uppercase tracking-[0.16em] text-slate-400 text-center">Alt Qty</th>
                  <th className="py-3 px-4 font-extrabold uppercase tracking-[0.16em] text-slate-400 text-center">Active Stock</th>
                  <th className="py-3 px-4 font-extrabold uppercase tracking-[0.16em] text-slate-400 text-center">Exp Date</th>
                  <th className="py-3 px-4 font-extrabold uppercase tracking-[0.16em] text-slate-400 text-right">Status</th>
                </tr>
              </thead>
              <tbody>
                {stockStatusList.map((item) => (
                  <tr key={item.id} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/50">
                    <td className="py-3 px-4 font-bold text-[#0d0d0d]">
                      {item.name}
                    </td>
                    <td className="py-3 px-4 text-slate-600 font-bold text-center">{item.maxCount || '—'}</td>
                    <td className="py-3 px-4 text-slate-600 font-bold text-center" style={{ color: item.statusType === 'error' ? '#ef4444' : '#f59e0b' }}>
                      {item.activeQty}
                    </td>
                    <td className={`py-3 px-4 font-mono text-center ${
                      item.isExpired ? 'text-rose-600 font-bold' : item.isExpiringSoon ? 'text-amber-600 font-bold' : 'text-slate-700 font-medium'
                    }`}>
                      {item.expiryDate || '—'}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className={`text-[11px] font-bold uppercase tracking-widest ${
                        item.statusType === 'error' ? 'text-rose-600' : 'text-amber-500'
                      }`}>
                        {item.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </motion.div>
  );
};
